/**
 * "제때 도착할까요?" — 이 카드가 답할 질문은 그거 하나입니다.
 *
 * 읽는 순서가 곧 판단 순서입니다.
 *   ① 결론   늦는지 아닌지. 이 화면에서 가장 큰 글자
 *   ② 근거   도착 예상과 예정을 나란히
 *   ③ 남은 것 거리와 소요, 얼마나 왔는지
 *   ④ 각주   무엇을 기준으로 한 숫자인지 (카드 밖)
 *
 * 처음에는 색 띠 셋을 위아래로 쌓았는데, 위계가 없어 알림 세 개처럼
 * 보였습니다. 숫자도 일곱 개나 됐습니다. 결론 하나만 크게 두고 나머지는
 * 평문으로 내렸습니다.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { IcClock, IcRun } from 'components/common/icons/EmojiIcons';
import type { TraceLive, TraceTrend } from 'hooks/useTraceLive';
import styles from './TraceLiveCard.module.css';

interface Props {
    live: TraceLive | null;
    trend: TraceTrend;
    /** 아직 안 나섰을 때 — 제목과 말투가 달라집니다 */
    staying?: boolean;
    /** 지금 머무는 곳 이름 (안 나섰을 때 부제로) */
    currentName?: string | null;
}

const fmtDistance = (m?: number): string => {
    if (typeof m !== 'number') return '';
    return m >= 1000 ? `${(m / 1000).toFixed(1)}km` : `${Math.round(m)}m`;
};

const fmtTime = (iso?: string | null): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export const TraceLiveCard: React.FC<Props> = ({ live, trend, staying, currentName }) => {
    const { t } = useTranslation();

    /* 위치 공유를 껐거나 목적지가 없으면 판정 자체가 성립하지 않습니다 */
    if (!live?.available) return null;

    const delay = live.delayMinutes;
    const late = typeof delay === 'number' && delay > 0;
    const knownDelay = typeof delay === 'number';

    const trendText: Record<TraceTrend, string> = {
        approaching: t('trace.approaching'),
        away: t('trace.away', { place: live.destinationName || '' }),
        stationary: t('trace.stationary'),
        arrived: t('trace.arrived'),
        unknown: staying ? t('trace.staying', { place: currentName || '' }) : t('trace.moving'),
    };

    return (
        <>
            <div className={styles.card}>
                {/* ① 결론 + ② 근거 */}
                <div className={late ? `${styles.verdict} ${styles.late}` : styles.verdict}>
                    <b className={styles.headline}>
                        {knownDelay
                            ? (late
                                ? t('trace.lateBy', { n: Math.abs(delay as number) })
                                : t('trace.onTime'))
                            : t('trace.etaOnly', { n: live.durationMinutes ?? 0 })}
                    </b>
                    {staying && late && <span className={styles.ifLeaveNow}>{t('trace.ifLeaveNow')}</span>}

                    {live.eta && (
                        <div className={styles.basis}>
                            <b>{fmtTime(live.eta)}</b> {t('trace.etaLabel')}
                        </div>
                    )}
                </div>

                {/* ③ 남은 것 */}
                <div className={styles.remain}>
                    <div className={styles.remainRow}>
                        <span className={styles.remainMain}>
                            {t('trace.toDestination', { place: live.destinationName || '' })}{' '}
                            <b>{fmtDistance(live.remainingM)}</b>
                        </span>
                        <span className={styles.remainSub}>
                            {t('trace.minutes', { n: live.durationMinutes ?? 0 })}
                        </span>
                    </div>

                    <div className={styles.trend}>
                        {trend === 'approaching' ? <IcRun size={12} /> : <IcClock size={12} />}
                        <span>{trendText[trend]}</span>
                    </div>
                </div>
            </div>

            {/* ④ 각주 — 카드 밖. 본문처럼 보이면 안 됩니다 */}
            {live.confidence === 'low' && (
                <p className={styles.note}>{t('trace.roughNote')}</p>
            )}
        </>
    );
};
